/**
 * polyfills-safari.ts
 * -------------------------------------------------------------------------
 * Polyfills pour Safari / iOS < 17.4.
 *
 * pdf.js (embarqué par ngx-extended-pdf-viewer >= 26/28) utilise deux API
 * JS modernes qui n'existent qu'à partir de Safari 17.4 :
 *   - Promise.withResolvers()   -> erreur "Promise.withResolvers is undefined"
 *   - AbortSignal.any()         -> erreur "AbortSignal.any is not a function"
 *
 * Ce fichier ajoute ces deux méthodes UNIQUEMENT si elles sont absentes
 * (feature detection via l'opérateur `in`, qui ne déclenche pas la
 * vérification de signature stricte de TypeScript, d'où l'absence d'erreur
 * TS2550 même avec une lib es2022).
 *
 * À importer TOUT EN HAUT de src/main.ts, AVANT bootstrapApplication().
 * -------------------------------------------------------------------------
 */

/* -------------------------------------------------------------------------
 * 1. Promise.withResolvers()
 * Spec: https://tc39.es/proposal-promise-with-resolvers/
 * Disponible nativement à partir de Safari 17.4 / Chrome 119 / Firefox 121.
 * ------------------------------------------------------------------------- */
if (!('withResolvers' in Promise)) {
  // On type la signature pour rester strict sous TypeScript (es2022 target).
  (
    Promise as unknown as {
      withResolvers: <T>() => {
        promise: Promise<T>;
        resolve: (value: T | PromiseLike<T>) => void;
        reject: (reason?: unknown) => void;
      };
    }
  ).withResolvers = function withResolvers<T>() {
    let resolve!: (value: T | PromiseLike<T>) => void;
    let reject!: (reason?: unknown) => void;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { promise, resolve, reject };
  };
}

/* -------------------------------------------------------------------------
 * 2. AbortSignal.any()
 * Spec: https://dom.spec.whatwg.org/#dom-abortsignal-any
 * Disponible nativement à partir de Safari 17.4 / Chrome 116 / Firefox 124.
 *
 * Le signal retourné s'annule dès que l'UN des signaux fournis s'annule.
 * On nettoie les listeners pour éviter toute fuite mémoire.
 * ------------------------------------------------------------------------- */
if (!('any' in AbortSignal)) {
  (
    AbortSignal as unknown as {
      any: (signals: Iterable<AbortSignal>) => AbortSignal;
    }
  ).any = function any(signals: Iterable<AbortSignal>): AbortSignal {
    const controller = new AbortController();
    const list = Array.from(signals);

    // Stocke les handlers pour pouvoir les retirer ensuite.
    const cleanups: Array<() => void> = [];

    const cleanup = () => {
      for (const fn of cleanups) fn();
      cleanups.length = 0;
    };

    const onAbort = (sourceSignal: AbortSignal) => {
      // Propage la "reason" du signal d'origine (comportement natif).
      controller.abort(sourceSignal.reason);
      cleanup();
    };

    for (const signal of list) {
      // Si un signal est déjà annulé, on annule immédiatement et on arrête.
      if (signal.aborted) {
        controller.abort(signal.reason);
        cleanup();
        break;
      }

      const handler = () => onAbort(signal);
      signal.addEventListener('abort', handler, { once: true });
      cleanups.push(() => signal.removeEventListener('abort', handler));
    }

    return controller.signal;
  };
}

export {}; // garantit que ce fichier est traité comme un module ES.
